import React, {useMemo, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {
    AspectRatio,
    Box,
    Flex,
    Img,
    Skeleton,
    Stack,
    Text,
    useMultiStyleConfig,
} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {useProducts} from '@salesforce/commerce-sdk-react'
import Link from '@salesforce/retail-react-app/app/components/link'
import {findImageGroupBy} from '../../utils/image-groups-utils'
import {getProductUrl} from '../../hooks/use-product-data'
import Checkbox from '../checkbox'
import DisplayPrice from '../display-price'
import InformativeLabel from '../informative-label/partials/InformativeLabel'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

const ProductVariantsRecommendations = ({
    recommendations,
    onSelectionChange,
    setIsSelectionPending,
}) => {
    const styles = useMultiStyleConfig('ProductVariantsRecommendations')

    const {currency: activeCurrency} = useCurrency()

    const [selectedProducts, setSelectedProducts] = useState({})

    const recommendedProducts = useMemo(() => {
        const recommendationIds = (recommendations || [])
            .map((rec) => rec?.recommendedItemId)
            .filter(Boolean)

        return [...new Set(recommendationIds)]
    }, [recommendations])

    const ids = recommendedProducts.join(',')

    const {data: productsData, isLoading} = useProducts(
        {
            parameters: {
                ids,
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'product-variants-recommendations',
            },
        },
        {
            enabled: recommendedProducts.length > 0,
        },
    )

    const products = useMemo(() => {
        return (productsData?.data || [])
            .filter((item) => {
                const hasVariants = item?.c_variants?.length > 0 || item?.variants?.length > 0
                const selectedVariant = item?.c_variants?.[0]
                const stockLevel = Number(
                    selectedVariant?.stock ?? item?.inventory?.stockLevel ?? 0,
                )
                const isInStock =
                    stockLevel > 0 &&
                    selectedVariant?.orderable !== false &&
                    item?.inventory?.orderable !== false

                return hasVariants && isInStock
            })
            .slice(0, 3)
    }, [productsData?.data])

    useEffect(() => {
        const newSelected = products.filter((p) => Boolean(selectedProducts[p.id]))
        onSelectionChange?.(newSelected)
        setIsSelectionPending(false)
    }, [selectedProducts, products])

    const updateSelection = (itemId, checked) => {
        setIsSelectionPending(true)
        setSelectedProducts((prev) => ({
            ...prev,
            [itemId]: checked,
        }))
    }

    if (!recommendedProducts.length) {
        return null
    }

    const isSkeletonVisible = isLoading || !products.length
    const skeletonItems = recommendedProducts.slice(0, 3)

    const getDisplayDescription = (item) => {
        const description = item?.shortDescription || item?.c_subscriptionShortDescription || ''
        return description.replace(/<[^>]*>/g, '').trim()
    }

    const getProductImage = (item) => {
        return (
            item?.image?.link ||
            item?.image?.disBaseLink ||
            findImageGroupBy(item?.imageGroups, {
                viewType: 'large',
                selectedVariationAttributes: item?.variationValues,
            })?.images?.[0]?.link ||
            ''
        )
    }

    const getPriceInfo = (item) => {
        const currentPrice = Number(item?.price || item?.c_variants?.[0]?.price || 0)
        const listPrice = Number(item?.priceMax || item?.listPrice || currentPrice)
        const isOnSale = listPrice > currentPrice && currentPrice > 0

        return {
            currentPrice,
            listPrice,
            isOnSale,
            isASet: false,
            isMaster: false,
            isRange: false,
        }
    }

    return (
        <Stack data-testid="product-variants-recommendations" {...styles.container}>
            <Text as="h2" {...styles.sectionTitle}>
                <FormattedMessage
                    id="product_view.label.complete_experience"
                    defaultMessage="Complete the experience"
                />
            </Text>
            <Stack {...styles.list}>
                {isSkeletonVisible
                    ? skeletonItems.map((id) => (
                          <Flex key={id} {...styles.card} {...styles.skeletonCard}>
                              <Flex {...styles.contentWrapper}>
                                  <Flex {...styles.imageNameContainer}>
                                      <Skeleton {...styles.skeletonImageWrapper} />
                                      <Stack {...styles.content}>
                                          <Flex {...styles.nameRow}>
                                              <Skeleton {...styles.skeletonName} />
                                              <Skeleton {...styles.skeletonDescription} />
                                          </Flex>
                                          <Flex {...styles.priceRow}>
                                              <Skeleton {...styles.skeletonPrice} />
                                          </Flex>
                                      </Stack>
                                  </Flex>
                                  <Box {...styles.checkboxWrapper}>
                                      <Skeleton {...styles.skeletonCheckbox} />
                                  </Box>
                              </Flex>
                          </Flex>
                      ))
                    : products.map((item) => {
                          const image = getProductImage(item)
                          const priceData = getPriceInfo(item)
                          const isSelected = Boolean(selectedProducts[item.id])
                          const productUrl = getProductUrl(item)
                          const productTitle =
                              item?.c_variantIdentifier ||
                              item?.name ||
                              item?.productName ||
                              item?.id

                          const handleCardClick = () => {
                              updateSelection(item.id, !selectedProducts[item.id])
                          }

                          const handleProductLinkClick = (event) => {
                              event.stopPropagation()
                          }

                          return (
                              <Flex
                                  key={item.id}
                                  {...styles.card}
                                  onClick={handleCardClick}
                                  cursor="pointer"
                              >
                                  <Flex {...styles.contentWrapper}>
                                      <Flex {...styles.imageNameContainer}>
                                          <Link to={productUrl} onClick={handleProductLinkClick}>
                                              <AspectRatio {...styles.imageWrapper} as="div">
                                                  <Img
                                                      src={image}
                                                      alt={item?.name || ''}
                                                      {...styles.image}
                                                  />
                                              </AspectRatio>
                                          </Link>

                                          <Stack {...styles.content}>
                                              <Flex {...styles.nameRow}>
                                                  <Link
                                                      to={productUrl}
                                                      onClick={handleProductLinkClick}
                                                      _hover={{textDecoration: 'underline'}}
                                                  >
                                                      <Text {...styles.name} textAlign="left">
                                                          {productTitle}
                                                      </Text>
                                                  </Link>

                                                  <Text {...styles.description}>
                                                      {getDisplayDescription(item)}
                                                  </Text>
                                              </Flex>

                                              <Flex {...styles.priceRow}>
                                                  <Text {...styles.currentPrice}>+</Text>
                                                  <DisplayPrice
                                                      priceData={priceData}
                                                      currency={item?.currency || activeCurrency}
                                                      productDetailPriceView={true}
                                                      productDetailCurrentPriceTextStyle="body-small"
                                                      productDetailListPriceTextStyle="body-small"
                                                      priceContainerProps={styles.priceRow}
                                                      currentPriceProps={styles.currentPrice}
                                                      listPriceProps={styles.listPrice}
                                                      discountProps={styles.discount}
                                                  />
                                              </Flex>
                                          </Stack>
                                      </Flex>

                                      <Box
                                          {...styles.checkboxWrapper}
                                          onClick={(e) => e.stopPropagation()}
                                      >
                                          <Checkbox
                                              variant="pdp-recommendation-checkbox"
                                              isChecked={isSelected}
                                              onChange={(e) => {
                                                  updateSelection(item.id, e.target.checked)
                                              }}
                                              aria-label={`Select ${
                                                  item?.name || item?.productName || item?.id
                                              }`}
                                          />
                                      </Box>
                                  </Flex>

                                  {item?.c_addOnBenefit && (
                                      <InformativeLabel
                                          label={item?.c_addOnBenefit}
                                          variant="addOnBenefit"
                                      />
                                  )}
                              </Flex>
                          )
                      })}
            </Stack>
        </Stack>
    )
}

ProductVariantsRecommendations.propTypes = {
    recommendations: PropTypes.array,
    onSelectionChange: PropTypes.func,
    setIsSelectionPending: PropTypes.func,
}

export default ProductVariantsRecommendations
